import React, { useEffect, useRef, useCallback, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import BpmnModeler from "bpmn-js/lib/Modeler";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import "bpmn-js/dist/assets/diagram-js.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-codes.css";
import "bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css";
import "./ProcessView1.css";

export default function ProcessView1() {
  const containerRef = useRef(null);
  const modelerRef = useRef(null);
  const history = useHistory();
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState("");

  // BPMN initialisation
  useEffect(() => {
    const additionalModules = [
      require("diagram-js/lib/features/palette").default,
      require("diagram-js/lib/features/context-pad").default,
      require("diagram-js/lib/features/hand-tool").default,
      require("diagram-js/lib/features/lasso-tool").default,
      require("diagram-js/lib/features/space-tool").default,
      require("diagram-js/lib/features/global-connect").default,
      require("diagram-js/lib/features/keyboard").default,
      require("diagram-js/lib/features/move").default,
      require("diagram-js/lib/features/resize").default,
      require("diagram-js/lib/features/snapping").default,
      require("bpmn-js/lib/features/modeling").default,
      require("bpmn-js/lib/features/replace").default,
    ];

    modelerRef.current = new BpmnModeler({
      container: containerRef.current,
      keyboard: { bindTo: window },
      additionalModules,
    });

    const initialDiagram = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                  xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  targetNamespace="http://bpmn.io/schema/bpmn">
  <bpmn:process id="Process_1" isExecutable="false">
    <bpmn:startEvent id="StartEvent_1" name="Start">
      <bpmn:outgoing>Flow_1</bpmn:outgoing>
    </bpmn:startEvent>
    <bpmn:task id="Task_1" name="Example Task">
      <bpmn:incoming>Flow_1</bpmn:incoming>
    </bpmn:task>
    <bpmn:sequenceFlow id="Flow_1" sourceRef="StartEvent_1" targetRef="Task_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="Process_1">
      <bpmndi:BPMNShape id="StartEvent_1_di" bpmnElement="StartEvent_1">
        <dc:Bounds x="150" y="150" width="36" height="36"/>
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="Task_1_di" bpmnElement="Task_1">
        <dc:Bounds x="240" y="138" width="100" height="60"/>
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="Flow_1_di" bpmnElement="Flow_1">
        <di:waypoint x="186" y="168"/>
        <di:waypoint x="240" y="168"/>
      </bpmndi:BPMNEdge>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

    modelerRef.current
      .importXML(initialDiagram)
      .then(() => {
        modelerRef.current.get("canvas").zoom("fit-viewport");
      })
      .catch((err) => console.error("BPMN import error:", err));

    return () => modelerRef.current.destroy();
  }, []);

  // Import BPMN file
  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const text = await file.text();
    try {
      await modelerRef.current.importXML(text);
      modelerRef.current.get("canvas").zoom("fit-viewport");
    } catch (err) {
      console.error("BPMN import error:", err);
    }
  };

  // Export BPMN XML
  const handleExport = async () => {
    try {
      const { xml } = await modelerRef.current.saveXML({ format: true });
      const blob = new Blob([xml], { type: "text/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "diagram.bpmn";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("XML export error:", err);
    }
  };

  // Reset diagram
  const handleReset = async () => {
    const { xml } = await modelerRef.current.saveXML({ format: true });
    await modelerRef.current.importXML(xml);
    modelerRef.current.get("canvas").zoom("fit-viewport");
  };

  // Export PDF + auto redirect
  const handleDownloadPDF = useCallback(async () => {
    try {
      const element = document.querySelector(".bpmn-wrapper");
      if (!element) {
        alert("Error: Diagram not found!");
        return;
      }

      setDownloading(true);
      window.scrollTo(0, 0);

      const canvas = await html2canvas(element, {
        scale: 2.5,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
      });

      const imgData = canvas.toDataURL("image/png", 1.0);
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      let position = 0;
      const pageHeight = pdf.internal.pageSize.getHeight();

      while (position < pdfHeight) {
        pdf.addImage(imgData, "PNG", 0, -position, pdfWidth, pdfHeight);
        position += pageHeight;
        if (position < pdfHeight) pdf.addPage();
      }

      pdf.save("process-view.pdf");

      setDownloading(false);
      setMessage("✅ PDF successfully downloaded!");

      setTimeout(() => {
        history.push("/physical-model-1");
      }, 1500);
    } catch (err) {
      console.error("PDF export error:", err);
      alert("Error during PDF generation.");
      setDownloading(false);
    }
  }, [history]);

  return (
    <>
      <div className="bpmn-wrapper">
        <div ref={containerRef} className="bpmn-container"></div>
      </div>

      <div className="toolbar">
        <button onClick={handleReset}>♻️ Reset</button>
        <button onClick={handleExport}>💾 Export your diagram</button>
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="pdf-download-btn"
        >
          {downloading ? "Downloading..." : "💾 Save your diagram"}
        </button>
      </div>

      {message && (
        <p
          style={{
            textAlign: "center",
            color: message.startsWith("✅") ? "green" : "black",
            marginTop: "10px",
            fontWeight: 500,
          }}
        >
          {message}
        </p>
      )}

      <div className="icam-navigation">
        <Link to="/functional-view-1">
          <button className="icam-nav-btn">Back</button>
        </Link>
        <button
          className="icam-nav-btn"
          onClick={handleDownloadPDF}
          disabled={downloading}
        >
          {downloading ? "Downloading..." : "Download & Continue"}
        </button>
      </div>
    </>
  );
}
